/**
 * One formatter per concept. Dates are UTC on the wire and Africa/Nairobi on
 * screen — the backend computes its "today" boundaries in the same zone, so a
 * card saying "today" and a row timestamp agree.
 */

const TZ = 'Africa/Nairobi'

const dateTimeFmt = new Intl.DateTimeFormat('en-KE', {
  year: 'numeric', month: 'short', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ,
})

const dateTimeSecFmt = new Intl.DateTimeFormat('en-KE', {
  year: 'numeric', month: 'short', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: TZ,
})

const dateFmt = new Intl.DateTimeFormat('en-KE', {
  year: 'numeric', month: 'short', day: '2-digit', timeZone: TZ,
})

const timeFmt = new Intl.DateTimeFormat('en-KE', {
  hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ,
})

const numberFmt = new Intl.NumberFormat('en-KE')

export function fmtDateTime(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : dateTimeFmt.format(d)
}

export function fmtDateTimeSec(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : dateTimeSecFmt.format(d)
}

export function fmtDate(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : dateFmt.format(d)
}

export function fmtTime(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : timeFmt.format(d)
}

export function fmtNumber(n?: number | null): string {
  return n === null || n === undefined || Number.isNaN(n) ? '—' : numberFmt.format(n)
}

export function fmtPercent(fraction?: number | null, digits = 1): string {
  if (fraction === null || fraction === undefined || Number.isNaN(fraction)) return '—'
  return `${(fraction * 100).toFixed(digits)}%`
}

export function fmtBytes(bytes?: number | null): string {
  if (!bytes && bytes !== 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`
}

/** "3 m ago" / "2 h ago" / "5 d ago". Falls back to a date past a week. */
export function fmtRelative(iso?: string | null): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const secs = Math.floor((Date.now() - then) / 1000)
  if (secs < 0) return fmtDateTime(iso)
  if (secs < 60) return 'just now'
  if (secs < 3600) return `${Math.floor(secs / 60)} m ago`
  if (secs < 86400) return `${Math.floor(secs / 3600)} h ago`
  if (secs < 604800) return `${Math.floor(secs / 86400)} d ago`
  return fmtDate(iso)
}

/**
 * SCREAMING_SNAKE → Title Case, for enum codes we have no display name for.
 * Where the backend sends a `displayName` alongside the code, use that instead —
 * the label belongs to the server.
 */
export function humanise(code?: string | null): string {
  if (!code) return '—'
  if (code === 'UNKNOWN' || code === '__UNKNOWN__') return 'Unknown'
  return code
    .toLowerCase()
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/** Short UUID for dense columns: first segment only, monospaced by the caller. */
export function shortId(id?: string | null): string {
  if (!id) return '—'
  return id.split('-')[0] ?? id.slice(0, 8)
}

/** ISO date (yyyy-mm-dd) → start/end of that day in Nairobi, as UTC instants. */
/**
 * The calendar day an instant falls on, in the deployment's zone, as `yyyy-MM-dd`.
 *
 * `iso.slice(0, 10)` is the tempting version and it is wrong by a day for every
 * event between midnight and 03:00 EAT — the wire format is UTC and Nairobi is
 * UTC+3. Anything that buckets instants into days has to go through this.
 */
const dayKeyFmt = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric', month: '2-digit', day: '2-digit', timeZone: TZ,
})

export function dayKey(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return dayKeyFmt.format(d)
}

export function dayStartIso(day: string): string {
  return new Date(`${day}T00:00:00+03:00`).toISOString()
}

/**
 * A `<input type="datetime-local">` value → an ISO instant the server can parse.
 *
 * `datetime-local` yields `2027-08-10T06:00` — no seconds and, crucially, **no zone**.
 * A Jackson `Instant` field rejects that outright, which surfaces as a bare
 * "Malformed request body" 400 with nothing naming the field.
 *
 * The wall clock is read as **East Africa Time**, not the browser's zone, matching
 * `dayStartIso` above. That is the right reading for this platform: polls open at a
 * gazetted local time in Kenya, so an administrator working from another country must
 * still be entering Kenyan time — and browser-local would silently shift it.
 *
 * Returns null for empty input so an optional field stays optional.
 */
export function eatInstant(localDateTime?: string | null): string | null {
  if (!localDateTime) return null
  // Tolerate both `YYYY-MM-DDTHH:mm` and `…:ss`, which some browsers emit.
  const withSeconds = localDateTime.length === 16 ? `${localDateTime}:00` : localDateTime
  const d = new Date(`${withSeconds}+03:00`)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

export function dayEndIso(day: string): string {
  // Exclusive upper bound: the backend documents `to` as exclusive.
  const d = new Date(`${day}T00:00:00+03:00`)
  d.setDate(d.getDate() + 1)
  return d.toISOString()
}
